import { useCallback, useEffect, useState } from "react";
import { LifeBuoy, ArrowLeft, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWallet } from "@/context/WalletContext";
import { API, AttachmentPicker, MessageThread, StatusBadge, fmtDate, shortAddr } from "@/components/SupportShared";

const SUBJECTS = ["Swap", "Perp", "AI Bots", "AI Yield Agent Vaults", "Flip", "Wallet", "User Experience", "Other"];

const NewTicketForm = ({ headers, onCreated }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!subject || !message.trim()) return toast.error("Pick a subject and type a message");
    setBusy(true);
    const r = await fetch(`${API}/api/support/tickets`, {
      method: "POST", headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ subject, message, attachments: files.map((f) => f.id) }),
    });
    setBusy(false);
    if (!r.ok) return toast.error("Could not submit ticket");
    const t = await r.json();
    toast.success(`Ticket #${t.number} created`);
    setSubject(""); setMessage(""); setFiles([]);
    onCreated(t);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 space-y-4" data-testid="support-new-ticket">
      <h2 className="font-heading text-base md:text-lg tracking-wider">NEW TICKET</h2>
      <Select value={subject} onValueChange={setSubject}>
        <SelectTrigger data-testid="support-subject-select"><SelectValue placeholder="Select a subject" /></SelectTrigger>
        <SelectContent>
          {SUBJECTS.map((s) => <SelectItem key={s} value={s} data-testid={`support-subject-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} maxLength={5000}
        placeholder="Describe the issue — include tx IDs, bot names or steps to reproduce." data-testid="support-message-input" />
      <AttachmentPicker headers={headers} files={files} onChange={setFiles} />
      <Button onClick={submit} disabled={busy} className="rounded-full px-6" data-testid="support-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Submit ticket
      </Button>
    </div>
  );
};

const TicketDetail = ({ ticket, headers, addr, onBack, onUpdated }) => {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = setInterval(async () => {
      const r = await fetch(`${API}/api/support/tickets/${ticket.number}`, { headers: { "X-STX-Address": addr } });
      if (r.ok) {
        const t = await r.json();
        if (t.messages.length !== ticket.messages.length || t.status !== ticket.status) onUpdated(t);
      }
    }, 20000);
    return () => clearInterval(id);
  }, [ticket.number, ticket.messages.length, ticket.status, addr]); // eslint-disable-line react-hooks/exhaustive-deps

  const reply = async () => {
    setBusy(true);
    const r = await fetch(`${API}/api/support/tickets/${ticket.number}/messages`, {
      method: "POST", headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ message, attachments: files.map((f) => f.id) }),
    });
    setBusy(false);
    if (!r.ok) return toast.error((await r.json()).detail || "Could not send");
    setMessage(""); setFiles([]);
    onUpdated(await r.json());
  };

  return (
    <div className="space-y-5" data-testid="support-ticket-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="support-detail-back">
        <ArrowLeft className="h-4 w-4" />My tickets
      </button>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-2xl font-black" data-testid="support-detail-number">#{ticket.number}</h2>
        <span className="text-white/80">{ticket.subject}</span>
        <StatusBadge status={ticket.status} testid="support-detail-status" />
      </div>
      <MessageThread messages={ticket.messages} viewerAddr={addr} />
      {ticket.status !== "closed" ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Add a reply…" data-testid="support-reply-input" />
          <AttachmentPicker headers={headers} files={files} onChange={setFiles} testid="support-reply-attach" />
          <Button onClick={reply} disabled={busy || (!message.trim() && !files.length)} className="rounded-full" data-testid="support-reply-button">Send reply</Button>
        </div>
      ) : <p className="text-sm text-muted-foreground" data-testid="support-closed-note">This ticket is closed. Open a new ticket if you need more help.</p>}
    </div>
  );
};

export default function Support() {
  const { isConnected, addresses, connectWallet, getAuthHeaders } = useWallet();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const headers = getAuthHeaders();
  const addr = addresses?.stx;

  const load = useCallback(async () => {
    if (!addr) return;
    const r = await fetch(`${API}/api/support/tickets`, { headers: { "X-STX-Address": addr } });
    if (r.ok) setTickets((await r.json()).tickets);
  }, [addr]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Support Ticket | STXOSHI"; }, []);

  const upsert = (t) => { setActive(t); setTickets((prev) => [t, ...prev.filter((x) => x.number !== t.number)]); };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="support-page">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3">
          <LifeBuoy className="h-8 w-8 text-primary" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-wider">SUPPORT</h1>
        </div>
        <p className="mt-3 text-muted-foreground">Log a ticket and track replies from the STXOSHI team.</p>
        {!isConnected || !addr ? (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="support-connect-required">
            <p className="text-white/80">Connect your wallet so we can link your ticket to your account.</p>
            <Button onClick={connectWallet} className="mt-4 rounded-full" data-testid="support-connect-wallet">Connect Wallet</Button>
          </div>
        ) : active ? (
          <div className="mt-8"><TicketDetail ticket={active} headers={headers} addr={addr} onBack={() => { setActive(null); load(); }} onUpdated={upsert} /></div>
        ) : (
          <div className="mt-8 space-y-8">
            <NewTicketForm headers={headers} onCreated={upsert} />
            <section data-testid="support-my-tickets">
              <h2 className="font-heading text-base md:text-lg tracking-wider">MY TICKETS <span className="text-xs text-muted-foreground">· {shortAddr(addr)}</span></h2>
              <div className="mt-4 space-y-2">
                {tickets.length === 0 && <p className="text-sm text-muted-foreground" data-testid="support-no-tickets">No tickets yet.</p>}
                {tickets.map((t) => (
                  <button key={t.number} type="button" onClick={() => setActive(t)} data-testid={`support-ticket-row-${t.number}`}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left hover:border-primary/50 transition-colors">
                    <div className="min-w-0">
                      <p className="font-semibold">#{t.number} · {t.subject}</p>
                      <p className="text-xs text-muted-foreground">Updated {fmtDate(t.updated_at)} · {t.messages.length} messages</p>
                    </div>
                    <StatusBadge status={t.status} />
                  </button>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
